import eventbus from '@app/eventbus'
import { onUnmounted } from 'vue'
import noty from '@lib/noty'

/**
 * Basic class for generating the backbones for dialogs to select items from lists. 
 * 
 * What is the reason for property setKeyOnOpen?
 * ******************************************************************************************************
 * Vue typically reuses the DOM as efficiently as possible. 
 * That is fine. But in case of dialogs, it means that once the dialog opened, and the components are mounted, 
 * the creation is finished.
 * When the dialog is closed, it is moved to shadow dom and when it is reopened, it is moved to DOM. 
 * This without going through the lifecycle again. 
 * 
 * In most cases this is perfectly fine, except when components used in the dialog must e.g. load new data on mount. 
 * Specificly, this is about datatables. When a dialog contains a datatable, which we typically do, the datatable is 
 * loaded once, and not again when the dialog is reopened for another select action. 
 * 
 * In this case, we force the dialog to go through all lifecycles by generating a unique key for the vue component.
 *     
 */
class clsSelectListDialog {
    setKeyOnOpen= true;
    dlgKey      = undefined;
    modelName   = null
    dt          = null;
    maxWidth    = "850px"
    open        = false
    tab         = 0
    cancel      = "Sluit"
    select      = "Selecteer"
    closeAfterSelect = true    
    
    multiselect = false
    fnMultiSelectCallback = null;
    multiselected = false; // Shortly set to true when any multi selection is made.
    
    cls         = "action-dialog dialog-select"
    icon        = "select"
    title       = "title"
    noSubTitle  = false

    noActions = false
    diabled = false
    fnReject      = null; 
    fnResolve     = null;
    isLoading     = false;

    // The html element is used when we start navigating via keyboard.
    // In this case, we use the html element of the dialog as a root for the operations.
    htmlElementDialog = null; 

    
    // Projects and possible other select lists support subselects. 
    // In this case, whenever a project is selected and the project has chapters, a chapter can be selected.
    // However, this is not desired in all cases. When no subselect should be used, set the noSubItemSelect option. 
    noSubItemSelect = false;

    // Default focusfield. 
    // Usage:
    //      <control ref="q"></control>
    // ...
    //    const q = ref()
    // ...
    //   new clsSelectListDialog(....., {defaultFocus: ()=>q.value}) 
    defaultFocus = null 

    xl = 12;    // The columns dedicated to show the body in - xtra large device 
    lg = 12;    // The columns dedicated to show the body in - large device 
    sm = 12;    // The columns dedicated to show the body in - small device 

    setUniqueDialogKey() {
        if (!window._sldlg) {
            window._sldlg = 1;
        }
        window._sldlg++;
        this.dlgKey = window._sldlg + 1;;
    }

    async closeDialog(force) {
        if (this.fnReject) {
            this.fnReject(null);
        }
        this.open = false;
    }
    openDialog() {
        if (this.setKeyOnOpen) {
            this.setUniqueDialogKey();
        }
        this.open = true;
        // Remove the context for a short period as the reference to the field is probably not yet resolved.
        setTimeout( ()=> this.setDefaultFocus(), 100)
        // this.setDefaultFocus();
    }
    setDefaultFocus() {
        if (!this.defaultFocus || !(this.defaultFocus instanceof Function)) {
            return;
        }
        let focusCtrl = this.defaultFocus();
        if (focusCtrl && focusCtrl.$el && focusCtrl.$el.getElementsByTagName) {
            var $el = focusCtrl.$el.getElementsByTagName('input');
            if ($el && $el.length && $el[0].focus) {
                $el[0].focus();
                $el[0].select();
            }
        }
    }

    get disabled() {
        return false;
    }

    // Overrides for functions like 'onBulkRemove', etc
    callbacks= {

    }
    /**
     * Get the selectable lines and use this.htmlElementDialog as the container html.
     * When no such container exists, there is nothing we can do.
     * @returns 
     */
    getSelectableLines() {
        if (!this.htmlElementDialog) {
            return null;
        }
        var selectables = this.htmlElementDialog.getElementsByClassName('fa-circle-plus')
        if (!selectables || !selectables.length) {
            return null;
        }
        // convert html collection to js array
        selectables = [...selectables];
        return selectables;
    }
    /**
     * Within this.htmlElementDialog, get the selected index.
     * @returns 
     */
    getSelectedIndex() {
        var lines = this.getSelectableLines();
        if (!lines) {
            return -1;
        }
        var selectedIndex = lines.findIndex( (line) => line && line.classList && line.classList.contains('select-list-selected'));
        return selectedIndex;
    }
    /**
     * Unselect all lines within this.htmlElementDialog
     * @returns 
     */
    unSelectLines() {
        if (!this.htmlElementDialog) {
            return; // no container
        }
        // Just remove all.
        var arrElements = [];
        var elements = this.htmlElementDialog.getElementsByClassName('select-list-selected');
        // As the elements htmlcollection keeps on getting updated when the class list for matching elements is modified, 
        // copy the elements in another array first. 
        for (var n = 0; n < elements.length; n++) {
            arrElements.push(elements[n]);
        }
        for (var n = 0; n < arrElements.length; n++  ) {
            var elem = arrElements[n];
            if (elem && elem.classList) {
                elem.classList.remove('select-list-selected')                
            }
        }
    }
    /**
     * Select the next available line
     * @returns 
     */
    selectNext() {
        var lines = this.getSelectableLines();
        if (!lines || !lines.length) {
            console.log('no more selectable lines')
            return;
        }
        var ix = this.getSelectedIndex();
        var ixNext = ix+1;
        if (ix >= (lines.length -1)) {
            ixNext = 0;
        }
        this.selectByIndex(ixNext, false, true);
    }
    /**
     * Select the formfield within the dialog - when available
     * This will only work when this.htmlElementDialog is set.
     */
    selectFormField() {
        if (!this.htmlElementDialog) {
            return;
        }
        var forms = this.htmlElementDialog.getElementsByTagName('form');
        if (forms && forms.length) {
            var form = forms[0];
            var firstInput = form.getElementsByTagName('input');
            if (firstInput && firstInput.length) {
                setTimeout( () => {
                    firstInput[0].select();
                    firstInput[0].focus();
                }, 10);
            }
        }
    }

    /**
     * Select the previous line and when the first is reached, jump to the input field.
     * @returns 
     */
    selectPrev() {
        var lines = this.getSelectableLines();
        if (!lines || !lines.length) {
            return;
        }        
        var ix = this.getSelectedIndex();
        this.unSelectLines(); // Before doing anything, clear any selection.

        if (ix < 0) { // Otherwise, no line is selected.Do not set the focus via up key.             
            return;
        }
        // When the focus goes beyond the first line, we want to focus on the first search field again.
        if (ix == 0) {
            this.selectFormField();
            return; // And we are done.
        }

        var ixPrev = ix-1;
        // Otherwise, select the previous line.
        this.selectByIndex(ixPrev, false, true);
    }
    /**
     * Select a line by index.
     * At this point - and then specifically when navigating via the keyboard -, the focus could be in 
     * the input in the searchform. Now, after pressing 'enter', the searchform is submitted again. 
     * 
     * This is typically unwanted as enter should now insert the selected line. 
     * So, whenever an input element is selected, set the focus to the dialog itself, hereby preventing
     * unexpected searches. 
     * 
     * Of course, exceptions of the rule.
     * When an input is visible in a line, like for example, the count field in select product dialog, 
     * we dont want to shift focus when a user clicks in the count input field. 
     * For this reason, the noFocusToDialog prevents changing the focus.  
     * 
     * Further more, we only want to scroll into view when the user navigates via keyboard. 
     * Otherwise, the line is already in view and we can not improve but we can mess things up.
     * 
     * @param {*} ix 
     * @returns 
     */
    selectByIndex(ix, noFocusToDialog, scrollIntoView) {
        if (!this.htmlElementDialog) {
            return;
        }

        // Unselect all lines first. (don't care whether they are selected via clicking or up/downing)
        this.unSelectLines(); 
        
        var lines = this.getSelectableLines();
        if (!lines || !lines.length) {
            return;
        }
        // When we are in an input field in the form and we press 'down', the first line is visually selected. 
        // However, the focus is still in the input control.
        // That is an unwanted situation because when we press enter now, we don't want to trigger a search action 
        // but we want to select the selected line. 
        // Therefore, at this point, always set the focus to the dialog. Does not matter whether we are in the input control
        // or not. 
        // 
        if (!noFocusToDialog) {
            this.htmlElementDialog.click(); 
            this.htmlElementDialog.focus();     
        }
        if (ix < 0 || ix >= lines.length) {
            return; // can not select
        }
        lines[ix].classList.add('select-list-selected'); 

        if (scrollIntoView) {

            // When the selected line is not visible,. scroll the line into view - but only when it is not visible.
            var container = lines[ix].closest('.v-data-table__wrapper');
            if (container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = lines[ix].getBoundingClientRect();            
                if (containerRect&&elementRect) {
                    const offsetTop = 32; // The header in the datatable
                    if (elementRect.top < (containerRect.top+offsetTop) || elementRect.bottom > containerRect.bottom) {
                        lines[ix].scrollIntoView({ behavior: 'smooth', block: 'center' });
        //                 lines[ix].closest('.v-data-table__wrapper').scrollTop = -40; || table
                    }    
                }
            }

        }
    }
    selectSelected() {
        if (!this.htmlElementDialog) {
            return;
        }
        var selected = this.htmlElementDialog.getElementsByClassName('select-list-selected')
        if (!selected || !selected.length) {
            return;
        }
        selected[0].click()        
    }

    /**
     * Set the dialog html element from the event. 
     * Returns true when success, false otherwise.
     * @param {*} evt 
     * @returns 
     */
    setHtmlElementFromEvent(evt) {
        var evtTarget = evt;
        if (!evtTarget.closest && evtTarget.target) {
            evtTarget = evt.target;
        }
        if (!evtTarget.closest) {
            this.htmlElementDialog = null; 
            return false;
        }
        this.htmlElementDialog = evtTarget.closest('.v-dialog');
        if (!this.htmlElementDialog) {
            return false;
        }
        return true; 
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // 
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////
    onKeyDown(evt, b, c) {
        if (evt && evt.key == "Escape") {
            this.onClose()
        }
        ////////////////////////////////////////////////////////////////////////////////////////////////////
        //
        // Yes. We handle the javascript events with plane javascript. 
        // On key up and down, scroll through the actual list. 
        //
        ////////////////////////////////////////////////////////////////////////////////////////////////////
        // Set the htmlElementDialog property. We can deduce it from the incoming event. 
        // Focus event has a target property.
        if (!this.setHtmlElementFromEvent(evt)) {
            return;
        }

        if (evt && evt.key == "ArrowDown") {
            this.selectNext();
        }
        else if (evt && evt.key == "ArrowUp") {
            this.selectPrev();            
        }
        else if (evt && evt.key == "Enter") {
            this.selectSelected();            
        }
        // let keyCode = $event.keyCode ? $event.keyCode : $event.which;
        // 38: key up, 40: key down
        // if (($event && $event.target && undefined != $event.target.selectionStart) && (keyCode == 38 || keyCode == 40)) {

        if (this.callbacks.onKeyDown) {
            this.callbacks.onKeyDown(evt)
        }
    }

    onClose() {
        if (this.callbacks.onClose) {
            this.callbacks.onClose()
        }
        else {
            this.closeDialog();
        }
    }
    async onBeforeResolve(items) {
        return items;
    }

    indicateMultiSelected() {
        this.multiselected = true;  

        setTimeout(()=>this.multiselected = false, 1000);
    }

    async onSelected(items, forceClose) {
        if (!items || !items.length) {
            noty.alert("U heeft geen regels geselecteerd.");
            return;
        }
        // When a multiselect callback is specified, each line is handled by the callback and 
        // the dialog is still open.
        if (this.fnMultiSelectCallback) {
            items = await this.onBeforeResolve(items);
            this.isLoading = true;
            try {
                await this.fnMultiSelectCallback(items, this);
            } finally {
                this.isLoading = false;
            }
            
            this.indicateMultiSelected();
            if (forceClose) {
                this.closeDialog();
            }
            return;
        }

        if (this.fnResolve) {
            items = await this.onBeforeResolve(items);
            this.fnResolve(items);
        }
        this.closeDialog();
    }
    onSelect() {
        if (this.callbacks.onSelect) {
            this.callbacks.onSelect()
        }
        else {
            this.onSelected(this.dt.selectedItems());
        }
    }

    
    onOpen(params) {
        if (this.callbacks.onOpen) {
            this.callbacks.onOpen(params, this)
        }

        this.openDialog();
        this.tab = 0;
    }

    evtRegistrations = []
    registerEvents() {
        var self = this;
        this.evtRegistrations.push(
            // fnReject, fnResolve, modelName
            eventbus.dialog.open.onPromised( (fnResolve, fnReject, modelName, params) => {    
                if (modelName != self.modelName) {
                    return;
                }
                self.onOpen(params);
                this.fnReject      = fnReject;
                this.fnResolve     = fnResolve;
                this.noSubItemSelect = params && (params.noSubItemSelect);
                
//            eventbus.dialog.open.on((modelName, params) => {
                if (params && (undefined !==params.multiselect)) {
                    this.multiselect = params.multiselect;
                }
                this.fnMultiSelectCallback = params.fnMultiSelectCallback || null;
                this.closeAfterSelect = (this.fnMultiSelectCallback ? false : true);

                this.keepOpen = !!this.fnMultiSelectCallback;
                if (this.multiselect) {
                    this.dt.setModeMultiSelect();
                } else {
                    this.dt.setModeSingleSelect( (item) => {
                        self.onSelected([item]);
                    });
                }
                // Clear the existing filter as it may contain values for the previous search action
                for (var key in this.dt.filter) {
                    this.dt.filter[key] = null;
                }
                // Exclude specific ids.                                
                this.dt.filter.exclude = params && params.exclude || [];
                // Set the provided filter values. 
                // Note that you can not just set random values. In the datatable constructor, the filterable fields need to be defined. 
                // For an example, see DialogSelectProject.vue
                if (params && params.filter) {
                    for (var key in params.filter) {
                        this.dt.filter[key] = params.filter[key];
                    }                                    
                }

                return "this.dlgKey";
            })
        )
    }
    cleanUp() {
        (this.evtRegistrations||[]).forEach( (fnUnRegister) => fnUnRegister() ) 
        this.dt.actionDblClick = null;
        this.dt.actionClick = null;
    }

    /**
     * Let double click in the datatable serve as single select.
     * Ignore this when the dbl click is in an input control. It is extremely annoying when the line gets selected
     * when trying to focus in e.g. an amount field.
     */
    setupDblClickHandler() {
        var self = this;
        this.dt.actionDblClick = function(evt, item) {

            if (evt && evt.target && evt.target.closest && evt.target.closest('.v-input')) {
                return;
            }
        

            self.onSelected([item], true /* force close */);        
        }
    }

    /**
     * Let single click in the datatable select the line.
     * Note that 'select the line' just sets a class in the html element which visually selects the line 
     * and which allows us to select the line later on via e.g. an enter key handler.
     */
    setupClickHandler() {
        var self = this;
        this.dt.actionClick = function(item, ix, evt) {
            if (!self.setHtmlElementFromEvent(evt)) {
                return;
            }    
            var noChangeFocus = false;
            // When we are in an input control and we are in the datatable, do not change focus.
            if (evt && evt.target && evt.target.closest) {
                if (evt.target.closest('input') && evt.target.closest('.v-data-table')) {
                    noChangeFocus = true;
                }
            }

            self.selectByIndex(ix, noChangeFocus);
        }
    }


    constructor(modelName, title, dt, options) {
        options = options||{}
        this.modelName = modelName;
        this.title = title;
        var self = this;
        this.dt = dt;
        this.registerEvents();
        this.setUniqueDialogKey();
        for (var key in options) {
            this[key] = options[key];
        }
        this.setupDblClickHandler();
        this.setupClickHandler();
        onUnmounted(() => {
            self.cleanUp();
        })
    }
}

export default clsSelectListDialog;